import type { ReactNode } from 'react'
import { Box, Divider, Drawer, IconButton, Stack, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'

const DEFAULT_WIDTH = '40vw'

export interface SideDrawerProps {
  title: string
  onClose: () => void
  children: ReactNode
  /**
   * Replaces the header's close button, for the Cancel/Save pair a form drawer ends up needing.
   * Whatever is passed owns closing the drawer as well.
   */
  actions?: ReactNode
  /** Anything a `width` accepts; the default suits a form of a few fields. */
  width?: string | number
}

/**
 * A right-anchored drawer with a titled header, a divider, and a scrolling body.
 *
 * Always open: the drawer is mounted by whatever decides it should be visible, so there is no
 * `open` prop to keep in step with that decision.
 */
export function SideDrawer({
  title,
  onClose,
  children,
  actions,
  width = DEFAULT_WIDTH,
}: SideDrawerProps) {
  return (
    <Drawer anchor="right" open onClose={onClose} sx={{ '& .MuiDrawer-paper': { width } }}>
      <Stack sx={{ height: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          {actions ?? (
            <IconButton onClick={onClose} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
        <Divider />
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>{children}</Box>
      </Stack>
    </Drawer>
  )
}
